import { Player } from '@lottiefiles/react-lottie-player';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ALERT, alertCallbackStore, API_KEYS } from '@flexo/reducers';
import Button from '../../button/button';
import Textfield from '../../textfield/textfield';
import styles from './../alert.module.scss';

const loader = '/loaderLottie.json';

export default function AlertCreateApiKey() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const content = t('widgets.apiKeys', { returnObjects: true }) as any;

  const [keyName, setKeyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = () => {
    alertCallbackStore.clearAll();
    dispatch(ALERT.ResetAlert({} as any));
  };

  const handleConfirm = useCallback(async () => {
    if (isSubmitting || !keyName.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await dispatch(
        API_KEYS.CreateApiKeyAsync({ name: keyName.trim() }) as any
      );

      alertCallbackStore.clearAll();
      dispatch(ALERT.ResetAlert({} as any));

      // Show the created key alert
      if (result?.payload?.createdKey) {
        alertCallbackStore.setData({
          apiKey: result.payload.createdKey.apiKey,
        });
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'ALERT_API_KEY_CREATED',
            },
          } as any)
        );
      }
    } catch (error) {
      console.error('AlertCreateApiKey: error creating key:', error);
      setIsSubmitting(false);
    }
  }, [dispatch, isSubmitting, keyName]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter' && !isSubmitting && keyName.trim()) {
        event.preventDefault();
        event.stopPropagation();
        handleConfirm();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting, handleConfirm, keyName]);

  return (
    <div>
      <div
        id="alert-create-api-key"
        className={`${styles.Alert} ${styles.Alert__Confirm__Credentials__Changes}`}
      >
        <div className={styles.Alert__Title}>{content.createTitle}</div>
        <div className={styles.Alert__Content}>{content.keyNameLabel}</div>

        <div
          className={styles.Alert__Content__Form}
          onClick={(event) => {
            const parent = event.currentTarget;
            const child = parent.querySelector('input');
            child?.focus();
          }}
        >
          <Textfield
            label=""
            name="keyName"
            type="text"
            placeholder={content.keyNamePlaceholder}
            value={keyName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setKeyName(e.target.value)
            }
            color="white"
          />
        </div>

        <div className={styles.Alert__Footer__Actions}>
          <Button
            variant="secondary"
            color="grey"
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Cancel}`}
            label={t('common.cancel', { defaultValue: 'Cancel' })}
            onClick={handleCancel}
          />
          <Button
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Confirm}`}
            variant="primary"
            color="secondary"
            label={
              isSubmitting ? (
                <Player
                  autoplay
                  loop
                  src={loader}
                  style={{ height: 20, width: 60 }}
                />
              ) : (
                t('common.create', { defaultValue: 'Create' })
              )
            }
            disabled={isSubmitting || !keyName.trim()}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </div>
  );
}
